import { isArray } from "@suse-o11y/is-array";
import { isPresent } from "@suse-o11y/is-present";
import type { Record } from "@suse-o11y/record";
import { isRight } from "fp-ts/es6/Either";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import omitBy from "lodash/omitBy";
import pickBy from "lodash/pickBy";
import xor from "lodash/xor";
import {
	type QueryParamConfig,
	decodeBoolean,
	decodeNumber,
	decodeObject,
	decodeString,
	encodeBoolean,
	encodeNumber,
	encodeObject,
	encodeString,
} from "serialize-query-params";
import { type SchemaOf, array, boolean, number, object, string } from "yup";

import { type NodeId, type QueryView, Type, type Version, type ViewEventFilters } from "~/models/dto";

import {
	TraversalData,
	generateComponentOfString,
	parseQuery,
} from "../../../../components/react/topology-query/TopologyQueryFunctions";
import { decodeUniqDelimitedArray, encodeUniqDelimitedArray } from "../../../../generic/queryParamUtils";
import { mergeSchemas } from "../../../../generic/Util";
import { NodeIdArrayParam, encodeNodeIdArray } from "../../../../routes/RouterUtils";

const decodeViewEventFilters = (obj: Record<string, string>): null | ViewEventFilters => {
	const fields = pickBy(
		{
			categories: decodeUniqDelimitedArray(obj.eventCategories, ","),
			types: decodeUniqDelimitedArray(obj.eventTypes, ","),
			sources: decodeUniqDelimitedArray(obj.eventSources, ","),
			tags: decodeUniqDelimitedArray(obj.eventTags, ","),
		},
		isPresent,
	);

	if (isEmpty(fields)) {
		return null;
	}

	return {
		_type: Type.ViewEventFilters,
		...fields,
	};
};

export type ViewShareableStateWithoutQuery = Omit<ViewShareableState, "query">;
export type ViewParamConfig = QueryParamConfig<undefined | Partial<ViewShareableStateWithoutQuery>>;

export const ViewParam: ViewParamConfig = {
	encode: ({
		groupedByDomains,
		groupedByLayers,
		groupedByRelations,
		showIndirectRelations,
		groupingEnabled,
		minimumGroupSize,
		eventFilters,
		tags,
		autoGrouping,
		connectedComponents,
		neighboringComponents,
	} = {}) => {
		const obj = pickBy(
			{
				groupDomains: encodeBoolean(groupedByDomains),
				groupLayers: encodeBoolean(groupedByLayers),
				groupRelations: encodeBoolean(groupedByRelations),
				indirectRelations: encodeBoolean(showIndirectRelations),
				grouping: encodeBoolean(groupingEnabled),
				groupSize: encodeNumber(minimumGroupSize),
				eventCategories: encodeUniqDelimitedArray(eventFilters?.categories, ","),
				eventTypes: encodeUniqDelimitedArray(eventFilters?.types, ","),
				eventSources: encodeUniqDelimitedArray(eventFilters?.sources, ","),
				eventTags: encodeUniqDelimitedArray(eventFilters?.tags, ","),
				tags: encodeUniqDelimitedArray(tags, ","),
				autoGrouping: encodeBoolean(autoGrouping),
				connectedComponents: encodeBoolean(connectedComponents),
				neighboringComponents: encodeBoolean(neighboringComponents),
			},
			isPresent,
		);

		if (isEmpty(obj)) {
			return undefined;
		}

		return encodeObject(obj, "--", "__");
	},
	decode: (value) => {
		const obj = decodeObject(value, "--", "__");

		if (obj == null) {
			return undefined;
		}

		const view = pickBy(
			{
				groupedByDomains: decodeBoolean(obj.groupDomains),
				groupedByLayers: decodeBoolean(obj.groupLayers),
				groupedByRelations: decodeBoolean(obj.groupRelations),
				showIndirectRelations: decodeBoolean(obj.indirectRelations),
				groupingEnabled: decodeBoolean(obj.grouping),
				minimumGroupSize: decodeNumber(obj.groupSize),
				eventFilters: decodeViewEventFilters(obj),
				tags: decodeUniqDelimitedArray(obj.tags, ","),
				autoGrouping: decodeBoolean(obj.autoGrouping),
				connectedComponents: decodeBoolean(obj.connectedComponents),
				neighboringComponents: decodeBoolean(obj.neighboringComponents),
			},
			isPresent,
		);

		return isEmpty(view) ? undefined : view;
	},
};

export const QueryParam: QueryParamConfig<string | undefined> = {
	encode: (value) => {
		if (value == null) {
			return undefined;
		}

		return encodeString(value);
	},
	decode: (value) => decodeString(value) ?? undefined,
};

export const DetachedFiltersParam: QueryParamConfig<Array<string> | undefined> = {
	encode: (value) => {
		if (value == null || value.length === 0) {
			return undefined;
		}

		return encodeUniqDelimitedArray(value, ",");
	},
	decode: (value) => decodeUniqDelimitedArray(value, ",") ?? undefined,
};

export type ExploreSubview = string | Array<NodeId>;

export const isExploreSubview = (value: unknown): value is ExploreSubview =>
	typeof value === "string" || isArray(value);

export const generateExploreSubviewQuery = (subview: ExploreSubview): undefined | string => {
	if (typeof subview === "string") {
		return subview;
	}

	return generateComponentOfString(subview);
};

export const generateViewComponentQuery = (componentIdentifier: string): string =>
	`identifier = "${componentIdentifier}"`;

const ExploreSubviewParam: QueryParamConfig<undefined | ExploreSubview> = {
	encode(value) {
		if (typeof value === "string") {
			return encodeString(value) || undefined;
		}

		if (isArray(value)) {
			return encodeObject({ selection: encodeNodeIdArray(value) }, "-", "_");
		}

		return undefined;
	},

	decode(valueStr) {
		const obj = decodeObject(valueStr, "-", "_");

		const selection = NodeIdArrayParam.decode(obj?.selection);
		if (selection != null) {
			return selection;
		}

		const query = decodeString(valueStr);
		if (query != null) {
			return query;
		}

		return undefined;
	},
};

const gridSettingsSchema = object({
	groupedByDomains: boolean(),
	groupedByLayers: boolean(),
});

export type GroupSettingsValues = Pick<
	ViewShareableState,
	"groupingEnabled" | "groupedByRelations" | "minimumGroupSize" | "autoGrouping"
>;

export const groupSettingsSchema = object({
	groupingEnabled: boolean(),
	groupedByRelations: boolean(),
	minimumGroupSize: number().when("groupingEnabled", {
		is: true,
		then: number().min(2, "Minimum group size should be a number greater than 1"),
		otherwise: number(),
	}),
});

const connectedComponentsSchema = object({
	connectedComponents: boolean(),
});

const neighboringComponentsSchema = object({
	neighboringComponents: boolean(),
});

const relationSchema = object({
	showIndirectRelations: boolean(),
});

const querySchema = object({
	query: string()
		.trim()
		.nullable()
		.test({
			name: "validQuery",
			test(query) {
				if (query == null || query === "") {
					return true;
				}
				const result = parseQuery(query);
				return isRight(result) ? true : this.createError({ message: result.value.reason });
			},
		}),
});

const eventTypesSchema = object({
	eventFilters: object().shape({
		tags: array().of(string()),
		categories: array().of(string()),
		types: array().of(string()),
	}),
});

const tagsSchema = object({
	tags: array().of(string()),
});

export const viewPointSchema = mergeSchemas(
	gridSettingsSchema,
	groupSettingsSchema,
	connectedComponentsSchema,
	neighboringComponentsSchema,
	relationSchema,
	querySchema,
	eventTypesSchema,
	tagsSchema,
) as SchemaOf<ViewShareableState>;

export type ViewShareableState = Pick<
	QueryView,
	| "groupedByDomains"
	| "groupedByLayers"
	| "groupedByRelations"
	| "showIndirectRelations"
	| "groupingEnabled"
	| "minimumGroupSize"
	| "query"
	| "tags"
	| "eventFilters"
	| "autoGrouping"
	| "connectedComponents"
	| "neighboringComponents"
>;

/**
 * If the comparing arrays have the same elements (order doesn't matter), return undefined.
 * Otherwise, return the next array.
 */
const diffArray = <T>(saved: Array<T>, next: undefined | Array<T>): undefined | Array<T> => {
	if (next == null) {
		return undefined;
	}

	const isDifferent = saved.length !== next.length || xor(saved, next).length > 0;

	return isDifferent ? next : undefined;
};

/**
 * If saved has the same values as next, return undefined.
 * Otherwise, return the next object without the same values as saved.
 */
const diffPlainObject = <TObj extends Record<string, any>>(
	saved: TObj,
	next: Partial<TObj>,
): undefined | Partial<TObj> => {
	const diff = omitBy<Partial<TObj>>(next, (value, key) => value === saved[key]);

	return isEmpty(diff) ? undefined : diff;
};

const diffViewEventFilters = (
	saved: ViewEventFilters,
	next: undefined | ViewEventFilters,
): undefined | ViewEventFilters => {
	if (next == null) {
		return undefined;
	}

	const diff: Partial<ViewEventFilters> = omitBy(
		{
			tags: diffArray(saved.tags ?? [], next.tags),
			types: diffArray(saved.types ?? [], next.types),
			categories: diffArray(saved.categories ?? [], next.categories),
			sources: diffArray(saved.sources ?? [], next.sources),
		},
		isUndefined,
	);

	if (isEmpty(diff)) {
		return undefined;
	}

	return {
		_type: Type.ViewEventFilters,
		...diff,
	};
};

type ViewGroupSettings = Pick<
	ViewShareableState,
	"groupingEnabled" | "autoGrouping" | "groupedByRelations" | "minimumGroupSize"
>;

/**
 * The grouping settings are tricky.
 * There are 4 flags/values determining just 4 different states, which produces a lot of impossible combinations.
 * The goal of this function is to avoid the impossible combinations, so a view does not get dirty because of them.
 *
 * Here is how we sorting it out in the UI:
 * @see src/main/routes/topology/visualizationSettingsOptions/GroupSettings.tsx
 */
const diffViewGroupSettings = (
	saved: Partial<ViewShareableState>,
	next: Partial<ViewGroupSettings>,
): undefined | Partial<ViewGroupSettings> => {
	// Auto Grouping is determined by the single flag
	if (next.autoGrouping) {
		return diffPlainObject(saved, {
			autoGrouping: next.autoGrouping,
		});
	}

	// No grouping is determined by the following `false` flags
	if (next.groupingEnabled === false) {
		return diffPlainObject(saved, {
			autoGrouping: next.autoGrouping,
			groupingEnabled: next.groupingEnabled,
		});
	}

	// Grouping by type+state or type+state+relation is determined by the whole object
	return diffPlainObject(saved, next);
};

const diffViewQuery = (saved: undefined | string, next: undefined | string): undefined | string => {
	if (next == null) {
		return undefined;
	}

	if (saved == null) {
		return next;
	}

	return TraversalData.normalizeSTQL(saved) === TraversalData.normalizeSTQL(next) ? undefined : next;
};

/**
 * ! The order of arguments is matter, see the examples below.
 *
 * The function returns deep difference between two ViewShareableState.
 * The resulting diff is a logical NOT and can be described as "what is different in the next ViewShareableState compared to the saved one".
 * Another words, the diff is a set of fields that present in both saved and next, but have different values.
 * Or another words, the diff is the next ViewShareableState without the same values as saved.
 *
 * If the next value is undefined, it is omitted from the diff.
 *
 * @example
 * diffViewShareableState(
 *  { query: "foo", groupingEnabled: true, autoGrouping: true },
 *  { query: "bar", groupingEnabled: true, autoGrouping: true }
 * ) == { query: "bar" }
 *
 * diffViewShareableState(
 *  { query: "foo", groupingEnabled: true, autoGrouping: true },
 *  { groupingEnabled: true, autoGrouping: true }
 * ) == undefined
 *
 * diffViewShareableState(
 *  { groupingEnabled: true, autoGrouping: true }
 *  { query: "foo", groupingEnabled: true, autoGrouping: true },
 * ) == { query: "foo" }
 *
 * diffViewShareableState(
 *  { query: "foo", groupingEnabled: true, autoGrouping: true },
 *  { groupingEnabled: false, autoGrouping: true }
 * ) == { groupingEnabled: false }
 *
 * diffViewShareableState(
 *  { groupingEnabled: false, autoGrouping: true }
 *  { query: "foo", groupingEnabled: true, autoGrouping: true },
 * ) == { query: "foo", groupingEnabled: true }
 *
 * diffViewShareableState(
 *  { query: "foo", groupingEnabled: true, autoGrouping: true },
 *  { query: "bar", groupingEnabled: undefined }
 * ) == { query: "bar" }
 *
 * @param savedView - the saved ViewShareableState. It might be a QueryView, which is a superset of ViewShareableState.
 * @param next - the next ViewShareableState
 *
 * @returns undefined when there is no difference, otherwise a diff Partial<ViewShareableState>
 */
export const diffViewShareableState = (
	savedView: Partial<ViewShareableState>,
	{
		query,
		eventFilters,
		tags,
		autoGrouping,
		groupingEnabled,
		groupedByRelations,
		minimumGroupSize,
		...primitives
	}: Partial<ViewShareableState>,
): undefined | Partial<ViewShareableState> => {
	// first find out if the query is different
	const diffQuery = diffViewQuery(savedView.query, query);
	// then omit same primitive values
	const diffPrimitives = diffPlainObject(savedView, primitives);
	// then arrays
	const diffTags = diffArray(savedView.tags ?? [], tags);
	// then event filters
	const diffEventFilters = diffViewEventFilters(savedView.eventFilters ?? DEFAULT_EVENT_FILTERS, eventFilters);
	// then group settings
	const diffGroupSettings = diffViewGroupSettings(savedView, {
		autoGrouping,
		groupingEnabled,
		groupedByRelations,
		minimumGroupSize,
	});

	const diff: Partial<ViewShareableState> = omitBy(
		{
			...diffPrimitives,
			...diffGroupSettings,
			query: diffQuery,
			tags: diffTags,
			eventFilters: diffEventFilters,
		},
		isUndefined,
	);

	return isEmpty(diff) ? undefined : diff;
};

export const mergeViewShareableState = <TTarget extends QueryView | Partial<ViewShareableState>>(
	target: TTarget,
	source: Partial<ViewShareableState>,
): TTarget => ({
	...target,
	...source,
	eventFilters:
		target.eventFilters == null
			? source.eventFilters
			: {
					...target.eventFilters,
					...source.eventFilters,
				},
});

export const DEFAULT_EVENT_FILTERS: ViewEventFilters = {
	_type: Type.ViewEventFilters,
	categories: [],
	tags: [],
	types: [],
	sources: [],
};

export const DEFAULT_VIEW_POINT: QueryView = {
	_type: Type.QueryView,
	groupedByDomains: true,
	groupedByLayers: true,
	groupedByRelations: true,
	showIndirectRelations: false,
	groupingEnabled: true,
	minimumGroupSize: 2,
	query: "",
	eventFilters: DEFAULT_EVENT_FILTERS,
	tags: [],
	autoGrouping: true,
	name: "",
	queryVersion: "" as Version,
	connectedComponents: false,
	neighboringComponents: false,
};

export const DEFAULT_COMPONENT_VIEW_POINT: QueryView = {
	...DEFAULT_VIEW_POINT,
	groupedByDomains: false,
	groupedByLayers: false,
	groupedByRelations: false,
	showIndirectRelations: false,
	groupingEnabled: false,
	autoGrouping: true,
	connectedComponents: true,
	neighboringComponents: true,
};

export const viewParamsMap = {
	view: ViewParam,
	query: QueryParam,
	explore: ExploreSubviewParam,
	detachedFilters: DetachedFiltersParam,

	/**
	 * The `originView` param is used to store the `?view` param value of the view that originated a subview.
	 * When a view does not have a subview, it stores parts of its state in the `?view` param.
	 * When a view has a subview, the subview stores parts of its state in the `?view` param as well.
	 *
	 * If the originated view is dirty, we should store the `?view` param value of the originated view in the `?originView` param.
	 * It allows us the following:
	 * 1. If a subview branches from a dirty view, we can compare the `?view` param value of the subview with the `?originView` param value to determine if the subview is dirty.
	 * 2. If a subview branches from a dirty view, we can restore the `?view` param of the view that originated the subview when the subview is closed.
	 */
	originView: ViewParam,

	/**
	 * The `originQuery` param is used to store the `?query` param value of the view that originated a subview.
	 * When a view does not have a subview, it stores parts of its state in the `?query` param.
	 * When a view has a subview, the subview stores parts of its state and chained subview in the `?query` param as well.
	 *
	 * If a subview branches from a dirty view, we can restore the `?query` param of the view that originated the subview when the subview is closed.
	 */
	originQuery: QueryParam,
};

export const DEFAULT_VIEW_REPLACEMENT_QUERY: string = `id = "*"`;
